import { ContractScalingSection, CopyTradingSection } from './ContractScaling';
import React, { useCallback, useEffect, useState } from 'react';
import {
  getTrades, getStrategyCells, approveCell, pauseCell, resumeCell, recomputeCells,
  getReviewSummary, getConsecStatus,
} from '../api';
import {
  CheckCircle, XCircle, PauseCircle, PlayCircle, RefreshCw,
  BarChart3, Target, Clock, Layers, Award, LogOut, TrendingUp, Inbox, Loader2, AlertTriangle,
} from 'lucide-react';
import { ClassBadge, EntryModeBadge } from './ClassBadge';
import { pnlColorClass } from '../utils/pnl';
import { PageHeader } from './ui/PageHeader';
import { SectionHeader } from './ui/SectionHeader';
import { KpiCard } from './ui/KpiCard';
import { EmptyState } from './ui/EmptyState';
import { TableWrapper, Thead, Th, Tbody, Tr, Td } from './ui/TableWrapper';
import {
  BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis,
  ResponsiveContainer, Tooltip as RTooltip, ReferenceLine, Cell, Legend,
} from 'recharts';

const MIN_TRADES = 25;
const WIN_RATE_THRESHOLD = 0.50;
const PF_THRESHOLD = 1.5;
const DD_THRESHOLD = 0.20;

const TIER_STYLES: Record<string, string> = {
  paper: 'bg-gray-800 text-gray-300 border-gray-700',
  probation: 'bg-blue-900 text-blue-300 border-blue-700',
  scaling: 'bg-purple-900 text-purple-300 border-purple-700',
  full: 'bg-green-900 text-green-300 border-green-700',
  rejected: 'bg-red-900 text-red-300 border-red-700',
  paused: 'bg-yellow-900 text-yellow-300 border-yellow-700',
};

function Check(val: boolean) {
  return val ? <CheckCircle size={14} className="text-green-400" /> : <XCircle size={14} className="text-gray-600" />;
}

const formatDuration = (seconds: number) => {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const toneForPnl = (v: number | null): 'positive' | 'negative' | 'neutral' => {
  if (v === null || v === undefined) return 'neutral';
  if (v > 0) return 'positive';
  if (v < 0) return 'negative';
  return 'neutral';
};

export default function GoLiveChecklist() {
  const [trades, setTrades] = useState<any[]>([]);
  const [cells, setCells] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [hideInactive, setHideInactive] = useState<boolean>(false);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([getTrades(), getStrategyCells()]);
      setTrades(t.data);
      const rawCells = c.data || [];
      const classOrder: Record<string, number> = { SMR: 0, MACRO_LIQ: 1, MACRO_ONLY: 2 };
      const modeOrder: Record<string, number> = { confirmation: 0, retest_any: 1, retest_deep: 2 };
      rawCells.sort((a: any, b: any) => {
        const ca = classOrder[a.signal_class] ?? 9;
        const cb = classOrder[b.signal_class] ?? 9;
        if (ca !== cb) return ca - cb;
        return (modeOrder[a.entry_mode] ?? 9) - (modeOrder[b.entry_mode] ?? 9);
      });
      setCells(rawCells);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleApprove = async (id: string) => {
    if (!window.confirm('Approve this cell for live trading at 25% size?')) return;
    setBusyId(id);
    try { await approveCell(id); await load(); }
    catch (e) { alert('Failed to approve'); }
    finally { setBusyId(null); }
  };

  const handlePause = async (id: string) => {
    const reason = window.prompt('Pause reason:') || 'manual';
    setBusyId(id);
    try { await pauseCell(id, reason); await load(); }
    catch (e) { alert('Failed to pause'); }
    finally { setBusyId(null); }
  };

  const handleResume = async (id: string) => {
    setBusyId(id);
    try { await resumeCell(id); await load(); }
    catch (e) { alert('Failed to resume'); }
    finally { setBusyId(null); }
  };

  const handleRecompute = async () => {
    setBusyId('recompute');
    try { await recomputeCells(); await load(); }
    catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  // Orphan rows (ghost/duplicate records from orphan recovery, ~30 of 190)
  // are excluded everywhere else this data is aggregated (analytics.py
  // _fetch_trades, /review/summary Overall). These top KPI cards were the
  // one place still including them -- fixed to match, using the same
  // signal_class === 'ORPHAN' test as the backend.
  const isOrphan = (t: any) => t.signal_class === 'ORPHAN' || (!t.signal_class && (t.notes || '').includes('AUTO-CREATED by orphan recovery'));
  const closed = trades.filter(t => t.status === 'closed' && !isOrphan(t));
  const total = closed.length;
  // Standardize on net P&L (net_pnl falling back to pnl) for every figure in
  // this row -- PF previously used raw pnl while Best/Worst used net_pnl,
  // mixing gross and net bases in the same card row.
  const netOf = (t: any) => t.net_pnl ?? t.pnl ?? 0;
  const grossWins = closed.filter(t => netOf(t) > 0).reduce((s, t) => s + netOf(t), 0);
  const grossLosses = Math.abs(closed.filter(t => netOf(t) <= 0).reduce((s, t) => s + netOf(t), 0));
  const profitFactor = grossLosses > 0 ? grossWins / grossLosses : grossWins > 0 ? 999 : 0;

  const bestTrade = total > 0 ? Math.max(...closed.map(netOf)) : 0;
  const worstTrade = total > 0 ? Math.min(...closed.map(netOf)) : 0;
  const holdSeconds = closed.filter(t => t.time_in_trade_seconds).map(t => t.time_in_trade_seconds);
  const avgHold = holdSeconds.length > 0 ? holdSeconds.reduce((s, n) => s + n, 0) / holdSeconds.length : 0;

  const visibleCells = hideInactive ? cells.filter((c: any) => (c.total_trades || 0) > 0) : cells;

  const pfTone: 'positive' | 'negative' | 'neutral' | 'default' =
    profitFactor === 0 ? 'neutral' : profitFactor >= 1.5 ? 'positive' : profitFactor >= 1 ? 'default' : 'negative';

  if (loading) return (
    <div>
      <PageHeader title="Performance" description="Live performance breakdown, per-class promotion controls, and execution settings." />
      <div className="flex flex-col items-center justify-center h-48 text-gray-500 gap-3">
        <Loader2 size={28} className="animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Performance"
        description="Live performance breakdown, per-class promotion controls, and execution settings."
      />

      <div className="space-y-8">
        <section className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Profit Factor"
              value={profitFactor > 0 ? (profitFactor === 999 ? '∝' : profitFactor.toFixed(2)) : '--'}
              tone={pfTone}
              sub="net wins / net losses, orphans excluded"
            />
            <KpiCard label="Avg Hold Time" value={avgHold > 0 ? formatDuration(Math.round(avgHold)) : '--'} />
            <KpiCard
              label="Best Trade"
              value={total > 0 ? `${bestTrade > 0 ? '+' : ''}$${bestTrade.toFixed(2)}` : '--'}
              tone={total > 0 ? toneForPnl(bestTrade) : 'neutral'}
            />
            <KpiCard
              label="Worst Trade"
              value={total > 0 ? `${worstTrade > 0 ? '+' : ''}$${worstTrade.toFixed(2)}` : '--'}
              tone={total > 0 ? toneForPnl(worstTrade) : 'neutral'}
            />
          </div>
        </section>

        <section>
          <PerformanceBreakdown />
        </section>

        <section className="space-y-4">
          <SectionHeader
            title="Per-Class Readiness"
            description={`Split-test thresholds: ${MIN_TRADES}+ trades | WR ≥ ${WIN_RATE_THRESHOLD * 100}% | PF ≥ ${PF_THRESHOLD} | DD ≤ ${DD_THRESHOLD * 100}% | Expectancy > 0`}
            action={
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={!hideInactive} onChange={e => setHideInactive(!e.target.checked)} />
                  Show inactive classes
                </label>
                <button onClick={handleRecompute} disabled={busyId === 'recompute'}
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 disabled:opacity-50">
                  <RefreshCw size={12} /> Recompute
                </button>
              </div>
            }
          />

          {visibleCells.length === 0 ? (
            <div className="bg-gray-900 rounded-lg border border-gray-800">
              <EmptyState
                icon={<Inbox size={28} />}
                message={hideInactive ? 'No classes with trades yet' : 'No strategy cells configured'}
                sub={hideInactive ? 'Toggle on to show inactive classes.' : undefined}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {visibleCells.map((c: any) => {
                const wrPass = c.win_rate >= WIN_RATE_THRESHOLD;
                const pfPass = c.profit_factor >= PF_THRESHOLD;
                const ddPct = c.gross_profit > 0 ? c.max_drawdown / c.gross_profit : 0;
                const ddPass = ddPct <= DD_THRESHOLD;
                const expPass = c.expectancy > 0;
                const tradesPass = c.total_trades >= MIN_TRADES;
                const progressPct = Math.min(100, (c.total_trades / MIN_TRADES) * 100);

                return (
                  <div key={c.id} className="bg-gray-900 rounded-lg border border-gray-800 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <ClassBadge signalClass={c.signal_class} />
                          <EntryModeBadge mode={c.entry_mode} />
                        </div>
                        <div className="text-xs text-gray-500">{c.instrument}</div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full uppercase border ${TIER_STYLES[c.status] || 'bg-gray-700'}`}>
                        {c.status} &nbsp; {(c.live_size_pct * 100).toFixed(0)}%
                      </span>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="text-gray-400">Trades</span>
                          <span className="font-mono text-gray-300">{c.total_trades} / {MIN_TRADES}</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded h-1.5">
                          <div className={`h-1.5 rounded ${tradesPass ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${progressPct}%` }} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1">{Check(tradesPass)} <span className="text-gray-400">{MIN_TRADES}+ trades</span></div>
                        <div className="flex items-center gap-1">{Check(wrPass)} <span className="text-gray-400" title="Win rate: % of closed trades that were profitable. Threshold: 50%+">WR {(c.win_rate * 100).toFixed(1)}%</span></div>
                        <div className="flex items-center gap-1">{Check(pfPass)} <span className="text-gray-400" title="Profit factor: gross wins / gross losses. Threshold: 1.5+">PF {c.profit_factor.toFixed(2)}</span></div>
                        <div className="flex items-center gap-1">{Check(ddPass)} <span className="text-gray-400" title="Max drawdown as % of gross profit. Threshold: 20% or less.">DD {(ddPct * 100).toFixed(1)}%</span></div>
                        <div className="flex items-center gap-1 col-span-2">{Check(expPass)} <span className="text-gray-400" title="Average dollar profit per trade. Must be positive.">Expectancy {c.expectancy.toFixed(2)}</span></div>
                      </div>
                      <div className="flex justify-between text-xs pt-2 border-t border-gray-800">
                        <span className="text-gray-500">Wins: {c.wins}</span>
                        <span className="text-gray-500">Losses: {c.losses}</span>
                        <span className="text-gray-500">Gross: ${c.gross_profit.toFixed(0)}</span>
                      </div>
                    </div>

                    {/* Chart 8: per-class equity sparkline (only when trades exist) */}
                    <ClassSparkline trades={trades} signalClass={c.signal_class} entryMode={c.entry_mode} />

                    <div className="flex gap-2">
                      {c.status === 'paper' && c.eligible_for_promotion && (
                        <button onClick={() => handleApprove(c.id)} disabled={busyId === c.id}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded text-xs font-bold bg-green-600 hover:bg-green-500 text-white disabled:opacity-50">
                          <PlayCircle size={14} /> Approve Live (25%)
                        </button>
                      )}
                      {c.status === 'paper' && !c.eligible_for_promotion && (
                        <button disabled className="flex-1 px-3 py-2 rounded text-xs bg-gray-800 text-gray-500 cursor-not-allowed">
                          Not yet eligible
                        </button>
                      )}
                      {['probation', 'scaling', 'full'].includes(c.status) && (
                        <button onClick={() => handlePause(c.id)} disabled={busyId === c.id}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded text-xs font-bold bg-yellow-900 hover:bg-yellow-800 text-yellow-300 border border-yellow-700 disabled:opacity-50">
                          <PauseCircle size={14} /> Pause
                        </button>
                      )}
                      {c.status === 'paused' && (
                        <button onClick={() => handleResume(c.id)} disabled={busyId === c.id}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50">
                          Resume to Paper
                        </button>
                      )}
                      {c.status === 'rejected' && (
                        <button disabled className="flex-1 px-3 py-2 rounded text-xs bg-red-900/30 text-red-400 cursor-not-allowed">
                          Rejected
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-4">
          <ContractScalingSection />
          <CopyTradingSection />
        </section>
      </div>
    </div>
  );
}

// ===== Performance Breakdown (copied from Review.tsx) =====

type SampleKey = 'all' | 'last_30' | 'last_7d' | 'last_30d' | 'criteria_era';
type SourceKey = 'all' | 'paper' | 'tradovate';

// 'criteria_era' = trades since the Phase B criteria-count gate replaced
// score-based gating (commit a33f6b0, 2026-07-10 20:48 UTC). Default view:
// blending pre/post-7/10 trades into "All Trades" mixes two different gate
// systems into one number, which is the wrong lens for judging how the
// *current* system is performing.
const SAMPLE_OPTIONS: { value: SampleKey; label: string }[] = [
  { value: 'criteria_era', label: 'Since Criteria Gate (7/10)' },
  { value: 'all', label: 'All Trades (incl. pre-7/10)' },
  { value: 'last_30', label: 'Last 30 Trades' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
];

const SOURCE_OPTIONS: { value: SourceKey; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'paper', label: 'Paper Only' },
  { value: 'tradovate', label: 'Tradovate Only' },
];

const SIGNAL_CLASS_ORDER = ['SMR', 'MACRO_LIQ', 'MACRO_ONLY', 'BREAKER_ONLY'];
const ENTRY_MODE_ORDER = ['confirmation', 'retest_any', 'retest_deep'];
const SMT_ORDER = ['dual', 'es_only', 'ym_only', 'none'];
const QUALITY_ORDER = ['<30', '30-49', '50-69', '70-84', '85+'];

const fmtPct = (n: number | undefined | null) => `${(n ?? 0).toFixed(1)}%`;
const fmtR = (n: number | undefined | null) => `${(n ?? 0).toFixed(2)}R`;
const fmtMoney = (n: number | undefined | null) => {
  const v = n ?? 0;
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

type Bucket = {
  count: number; win_rate: number; avg_r: number; total_pnl: number; avg_pnl: number; wins: number; losses: number;
};

type ExitBucket = { count: number; total_pnl?: number; avg_pnl?: number; total_loss?: number; avg_loss?: number };

type Summary = {
  sample: string; source: string; generated_at: string;
  overall: Bucket;
  by_signal_class: Record<string, Bucket>;
  by_entry_mode: Record<string, Bucket>;
  by_macro_window: Record<string, Bucket>;
  by_smt_strength_15m: Record<string, Bucket>;
  by_smt_strength_1m: Record<string, Bucket>;
  loss_distribution: Record<string, ExitBucket>;
  exit_distribution: Record<string, ExitBucket>;
  by_quality_score: Record<string, Bucket>;
  by_criteria_count: Record<string, Bucket>;
};

function PerformanceBreakdown() {
  const [sample, setSample] = useState<SampleKey>('criteria_era');
  const [source, setSource] = useState<SourceKey>('all');
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [consec, setConsec] = useState<{ consecutive_losses: number; execution_paused: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getReviewSummary(sample, source);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sample, source]);

  const loadConsec = useCallback(async () => {
    try {
      const r = await getConsecStatus();
      setConsec(r.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Consecutive-loss gate state changes independently of the sample/source
  // filters above (it's live engine state, not a trade-history rollup) --
  // poll it on its own light cadence.
  useEffect(() => { loadConsec(); const id = setInterval(loadConsec, 30000); return () => clearInterval(id); }, [loadConsec]);

  const count = data?.overall?.count ?? 0;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Performance Breakdown"
        action={
          <div className="flex items-center gap-2">
            <select value={sample} onChange={(e) => setSample(e.target.value as SampleKey)}
              className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-xs">
              {SAMPLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={source} onChange={(e) => setSource(e.target.value as SourceKey)}
              className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-xs">
              {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 border border-gray-700 rounded px-3 py-1.5 text-xs font-medium">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        }
      />

      {loading && !data ? (
        <div className="bg-gray-900 rounded-lg border border-gray-800">
          <div className="flex flex-col items-center justify-center h-32 text-gray-500 gap-2">
            <Loader2 size={24} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : count === 0 ? (
        <div className="bg-gray-900 rounded-lg border border-gray-800">
          <EmptyState
            icon={<BarChart3 size={28} />}
            message="No closed trades yet"
            sub="Performance data will appear once trades close."
          />
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <OverallCard overall={data.overall} />
          {consec && <ConsecLossCard status={consec} />}
          <ClassCard title="By Signal Class" icon={Layers} order={SIGNAL_CLASS_ORDER} data={data.by_signal_class} labelHeader="Class" />
          <ClassCard title="By Entry Mode" icon={Target} order={ENTRY_MODE_ORDER} data={data.by_entry_mode} labelHeader="Mode" />
          <CriteriaCountCard data={data.by_criteria_count} />
          <MacroWindowCard data={data.by_macro_window} />
          {/* Quality score no longer gates or sizes trades (retired by the
              2026-07-10 criteria-count gate) -- demoted next to Macro Window
              for a paired row, kept for historical/pre-7/10 reference only. */}
          <QualityCard data={data.by_quality_score} />
          <SmtCombinedCard data15={data.by_smt_strength_15m} data1={data.by_smt_strength_1m} span="full" />
          <ExitDistributionCard exits={data.exit_distribution} losses={data.loss_distribution} />
        </div>
      ) : null}
    </div>
  );
}

function CardShell({ title, icon: Icon, subtitle, span, children }: any) {
  return (
    <div className={`bg-gray-900 rounded-lg border border-gray-800 p-5 hover:border-gray-700 transition-colors ${span === 'full' ? 'lg:col-span-2' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-gray-500" />}
        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      <div className={subtitle ? '' : 'mt-3'}>{children}</div>
    </div>
  );
}

function OverallCard({ overall }: { overall: Bucket }) {
  return (
    <CardShell title="Overall Performance" icon={TrendingUp} span="full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-3xl font-bold font-mono text-white">{fmtPct(overall.win_rate)}</div>
          <div className="text-xs text-gray-500 mt-1">win rate</div>
          <div className="text-xs text-gray-400 mt-2">
            <span className="text-green-400 font-bold">{overall.wins}W</span>
            <span className="text-gray-600 mx-1">/</span>
            <span className="text-red-400 font-bold">{overall.losses}L</span>
            <span className="text-gray-500"> · {overall.count} trades</span>
          </div>
        </div>
        <div>
          <div className={`text-3xl font-bold font-mono ${pnlColorClass(overall.avg_r)}`}>{fmtR(overall.avg_r)}</div>
          <div className="text-xs text-gray-500 mt-1">average</div>
          <div className="text-xs text-gray-400 mt-2">
            <span className={`font-mono font-bold ${pnlColorClass(overall.total_pnl)}`}>{fmtMoney(overall.total_pnl)}</span>
            <span className="text-gray-500"> total P&L · </span>
            <span className={`font-mono ${pnlColorClass(overall.avg_pnl)}`}>{fmtMoney(overall.avg_pnl)}</span>
            <span className="text-gray-500"> avg/trade</span>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function winRateBarColor(wr: number, bestWr: number) {
  if (wr === bestWr && wr > 0) return 'bg-green-900/30';
  if (wr > 0 && wr < 40) return 'bg-red-900/30';
  return '';
}

function ClassCard({ title, icon, order, data, labelHeader }: {
  title: string; icon: any; order: string[]; data: Record<string, Bucket>; labelHeader: string;
}) {
  const rows = order.map((key) => ({ key, bucket: data[key] })).filter((r) => r.bucket && r.bucket.count > 0);
  const bestWr = rows.reduce((max, r) => Math.max(max, r.bucket.win_rate), 0);
  const maxCount = rows.reduce((max, r) => Math.max(max, r.bucket.count), 0);

  return (
    <CardShell title={title} icon={icon}>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <TableWrapper inset>
          <Thead>
            <Th>{labelHeader}</Th>
            <Th className="text-right">Count</Th>
            <Th className="text-right">Win Rate</Th>
            <Th className="text-right">Avg R</Th>
          </Thead>
          <Tbody>
            {rows.map(({ key, bucket }) => {
              const bg = winRateBarColor(bucket.win_rate, bestWr);
              const barPct = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
              return (
                <Tr key={key} className={bg}>
                  <Td className="font-medium text-gray-200">{key}</Td>
                  <Td className="text-right font-mono relative">
                    <div className="relative">
                      <div className="absolute inset-y-0 right-0 bg-blue-500/10 rounded" style={{ width: `${barPct}%` }} />
                      <span className="relative">{bucket.count}</span>
                    </div>
                  </Td>
                  <Td className={`text-right font-mono font-bold ${
                    bucket.win_rate === bestWr && bucket.win_rate > 0 ? 'text-green-400' :
                      bucket.win_rate < 40 ? 'text-red-400' : 'text-gray-300'
                  }`}>{fmtPct(bucket.win_rate)}</Td>
                  <Td className={`text-right font-mono ${pnlColorClass(bucket.avg_r)}`}>{fmtR(bucket.avg_r)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableWrapper>
      )}
    </CardShell>
  );
}

// Chart 5: replaces the by-macro-window table with a horizontal bar chart,
// length = total P&L, color = win-rate bucket. Sorted by total P&L desc.
function MacroWindowCard({ data }: { data: Record<string, Bucket> }) {
  const rows = Object.entries(data).filter(([, b]) => b && b.count > 0)
    .map(([key, bucket]) => ({
      window: key,
      total_pnl: Number((bucket.total_pnl ?? 0).toFixed(2)),
      win_rate: (bucket.win_rate ?? 0) / 100, // backend returns percent (0-100)
      count: bucket.count,
    }))
    .sort((a, b) => b.total_pnl - a.total_pnl);
  return (
    <CardShell title="By Macro Window" icon={Clock}>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <ResponsiveContainer width="100%" height={rows.length * 32 + 50}>
          <BarChart layout="vertical" data={rows} margin={{ top: 6, right: 12, bottom: 4, left: 4 }}>
            <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis type="category" dataKey="window" width={120} tick={{ fontSize: 10, fill: '#6b7280' }} />
            <RTooltip
              formatter={(v: any, _name: any, item: any) => {
                if (item?.dataKey === 'total_pnl') return [`$${Number(v).toFixed(2)}`, 'Total P&L'];
                return [String(v), String(_name)];
              }}
              labelFormatter={(label, payload) => {
                const p = payload?.[0]?.payload as any;
                return p ? `${label} (${p.count} trades, ${(p.win_rate * 100).toFixed(0)}% WR)` : String(label);
              }}
              contentStyle={{ background: '#1f2937', border: '1px solid #374151', fontSize: 12, color: '#f3f4f6' }}
            />
            <ReferenceLine x={0} stroke="#374151" />
            <Bar dataKey="total_pnl">
              {rows.map((entry, i) => (
                <Cell key={i} fill={
                  entry.win_rate > 0.5 ? '#4ade80' :
                  entry.win_rate > 0.33 ? '#facc15' :
                  '#f87171'
                } />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardShell>
  );
}

function SmtTable({ data }: { data: Record<string, Bucket> }) {
  const rows = SMT_ORDER.map((key) => ({ key, bucket: data[key] })).filter((r) => r.bucket && r.bucket.count > 0);
  if (rows.length === 0) return <div className="text-xs text-gray-600 py-3 text-center">No data</div>;
  return (
    <TableWrapper inset>
      <Thead>
        <Th>SMT</Th>
        <Th className="text-right">Count</Th>
        <Th className="text-right">Win Rate</Th>
        <Th className="text-right">Avg R</Th>
        <Th className="text-right">Total P&L</Th>
      </Thead>
      <Tbody>
        {rows.map(({ key, bucket }) => (
          <Tr key={key}>
            <Td className="font-medium text-gray-200">{key}</Td>
            <Td className="text-right font-mono">{bucket.count}</Td>
            <Td className="text-right font-mono">{fmtPct(bucket.win_rate)}</Td>
            <Td className={`text-right font-mono ${pnlColorClass(bucket.avg_r)}`}>{fmtR(bucket.avg_r)}</Td>
            <Td className={`text-right font-mono font-bold ${pnlColorClass(bucket.total_pnl)}`}>{fmtMoney(bucket.total_pnl)}</Td>
          </Tr>
        ))}
      </Tbody>
    </TableWrapper>
  );
}

function SmtCombinedCard({ data15, data1, span }: { data15: Record<string, Bucket>; data1: Record<string, Bucket>; span?: string }) {
  const [tab, setTab] = React.useState<'15m' | '1m'>('15m');
  return (
    <CardShell title="SMT Correlation" icon={Award} subtitle="Does dual SMT outperform single? Toggle 15m vs 1m timeframe." span={span}>
      <div className="flex gap-2 mb-3">
        {(['15m', '1m'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {t}
          </button>
        ))}
      </div>
      <SmtTable data={tab === '15m' ? data15 : data1} />
    </CardShell>
  );
}

// Chart 6: quality score bucket -- bars for total P&L, line for win rate.
// Live consecutive-loss gate state (engine/signals/criteria_gate.py):
// >=3 losses requires 4-of-4 criteria to trade; >=4 (or a manually-reset
// pause latch) blocks all signals until reset via POST /risk/consec-reset.
function ConsecLossCard({ status }: { status: { consecutive_losses: number; execution_paused: boolean } }) {
  const { consecutive_losses: n, execution_paused: paused } = status;
  const level = paused ? 'blocked' : n >= 3 ? 'tightened' : 'normal';
  const cls = level === 'blocked' ? 'border-red-800 bg-red-950/30' : level === 'tightened' ? 'border-amber-800 bg-amber-950/20' : 'border-gray-800';
  const textCls = level === 'blocked' ? 'text-red-300' : level === 'tightened' ? 'text-amber-300' : 'text-gray-200';
  const label = paused ? 'EXECUTION PAUSED (4+ losses)' : n >= 3 ? 'Gate tightened: 4-of-4 required' : 'Normal gate (3-of-4 eligible)';
  return (
    <div className={`bg-gray-900 rounded-lg border p-5 hover:border-gray-700 transition-colors ${cls}`}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={14} className={level === 'normal' ? 'text-gray-500' : textCls} />
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Consecutive Loss Gate</span>
      </div>
      <div className="text-[11px] text-gray-500 mb-3">Live engine state -- not scoped to the sample/source filters above.</div>
      <div className="flex items-baseline gap-3">
        <span className={`text-3xl font-bold ${textCls}`}>{n}</span>
        <span className="text-xs text-gray-500">consecutive loss{n === 1 ? '' : 'es'}</span>
      </div>
      <div className={`text-xs font-medium mt-2 ${textCls}`}>{label}</div>
    </div>
  );
}

const CRITERIA_COUNT_ORDER = ['4_of_4', '3_of_4', '2_of_4', '1_of_4', '0_of_4', 'pre_criteria_era'];
const CRITERIA_COUNT_LABELS: Record<string, string> = {
  '4_of_4': '4-of-4', '3_of_4': '3-of-4', '2_of_4': '2-of-4 (below gate)',
  '1_of_4': '1-of-4 (below gate)', '0_of_4': '0-of-4 (below gate)',
  'pre_criteria_era': 'Pre-7/10 (old gate)',
};

function CriteriaCountCard({ data }: { data: Record<string, Bucket> }) {
  const rows = CRITERIA_COUNT_ORDER.map((key) => {
    const b = data[key];
    if (!b || b.count === 0) return null;
    return {
      bucket: CRITERIA_COUNT_LABELS[key] ?? key,
      total_pnl: Number((b.total_pnl ?? 0).toFixed(2)),
      win_rate: (b.win_rate ?? 0) / 100,
      count: b.count,
    };
  }).filter((x): x is NonNullable<typeof x> => x !== null);

  return (
    <CardShell title="By Criteria Count" icon={Award} subtitle="The live gate since 7/10/2026: 4-of-4 aligned sizes 2 contracts, 4-of-4 counter-bias / 3-of-4 size 1, 2-or-fewer are hard-blocked (should not appear below except from the known pre-7/10 data gap).">
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={rows} margin={{ top: 6, right: 4, bottom: 4, left: 4 }}>
            <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis yAxisId="pnl" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis yAxisId="wr" orientation="right" domain={[0, 1]}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
              tick={{ fontSize: 10, fill: '#6b7280' }} />
            <RTooltip
              contentStyle={{ background: '#1f2937', border: '1px solid #374151', fontSize: 12, color: '#f3f4f6' }}
              formatter={(value: any, name: any) => {
                if (name === 'win_rate') return [`${Math.round(Number(value) * 100)}%`, 'Win Rate'];
                if (name === 'total_pnl') return [fmtMoney(Number(value)), 'Total P&L'];
                return [value, name];
              }}
            />
            <Bar yAxisId="pnl" dataKey="total_pnl" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            <Line yAxisId="wr" type="monotone" dataKey="win_rate" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <div className="mt-2 text-[10px] text-gray-600 text-center">
        {rows.map((r) => `${r.bucket}: ${r.count}`).join(' · ')}
      </div>
    </CardShell>
  );
}

function QualityCard({ data }: { data: Record<string, Bucket> }) {
  const rows = QUALITY_ORDER.map((key) => {
    const b = data[key];
    if (!b || b.count === 0) return null;
    return {
      bucket: key,
      total_pnl: Number((b.total_pnl ?? 0).toFixed(2)),
      win_rate: (b.win_rate ?? 0) / 100,
      count: b.count,
    };
  }).filter((x): x is NonNullable<typeof x> => x !== null);

  return (
    <CardShell title="By Quality Score Bucket" icon={Award} subtitle="Historical reference only -- quality_score no longer gates or sizes trades (replaced by the criteria-count gate, 7/10/2026).">
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={rows} margin={{ top: 6, right: 4, bottom: 4, left: 4 }}>
            <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis yAxisId="pnl" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis yAxisId="wr" orientation="right" domain={[0, 1]}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
              tick={{ fontSize: 10, fill: '#6b7280' }} />
            <RTooltip
              contentStyle={{ background: '#1f2937', border: '1px solid #374151', fontSize: 12, color: '#f3f4f6' }}
              formatter={(v: any, name: any) => {
                if (name === 'win_rate') return [`${(Number(v) * 100).toFixed(1)}%`, 'Win Rate'];
                if (name === 'total_pnl') return [`$${Number(v).toFixed(2)}`, 'Total P&L'];
                return [String(v), String(name)];
              }}
            />
            <ReferenceLine y={0} yAxisId="pnl" stroke="#374151" strokeDasharray="3 3" />
            <Bar yAxisId="pnl" dataKey="total_pnl">
              {rows.map((e, i) => (
                <Cell key={i} fill={e.total_pnl >= 0 ? '#4ade80' : '#f87171'} />
              ))}
            </Bar>
            <Line yAxisId="wr" type="monotone" dataKey="win_rate" stroke="#60a5fa" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </CardShell>
  );
}

// Chart 7: exit distribution as two stacked bars (Winners / Losers),
// stacked by exit type. Keys are discovered from the data so we do not
// hardcode exit_type values the backend may not produce.
const EXIT_COLORS = ['#4ade80', '#60a5fa', '#facc15', '#fb923c', '#c084fc', '#9ca3af', '#f87171', '#22d3ee'];
function ExitDistributionCard({ exits, losses }: { exits: Record<string, ExitBucket>; losses: Record<string, ExitBucket> }) {
  const winnerKeys = Object.keys(exits || {}).filter(k => (exits[k]?.count ?? 0) > 0);
  const loserKeys = Object.keys(losses || {}).filter(k => (losses[k]?.count ?? 0) > 0);
  const hasAny = winnerKeys.length > 0 || loserKeys.length > 0;

  const winnersTable = Object.entries(exits || {})
    .filter(([, b]) => (b?.total_pnl ?? 0) > 0)
    .sort((a, b) => (b[1].total_pnl ?? 0) - (a[1].total_pnl ?? 0));
  const losersTable = Object.entries(losses || {})
    .filter(([, b]) => b && b.count > 0)
    .sort((a, b) => (a[1].total_loss ?? 0) - (b[1].total_loss ?? 0));

  return (
    <CardShell title="Exit Distribution" icon={LogOut} span="full">
      {!hasAny ? (
        <div className="text-xs text-gray-600 py-6 text-center">No exit data yet</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs text-green-400 font-semibold uppercase tracking-wider mb-2">Winners</h4>
          {winnersTable.length === 0 ? (
            <div className="text-xs text-gray-600 py-4 text-center">No winning exits</div>
          ) : (
            <TableWrapper inset>
              <Thead>
                <Th>Exit</Th>
                <Th className="text-right">Count</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">Avg</Th>
              </Thead>
              <Tbody>
                {winnersTable.map(([key, b]) => (
                  <Tr key={key}>
                    <Td className="font-mono text-gray-200">{key}</Td>
                    <Td className="text-right font-mono">{b.count}</Td>
                    <Td className="text-right font-mono text-green-400 font-bold">{fmtMoney(b.total_pnl)}</Td>
                    <Td className="text-right font-mono text-green-400">{fmtMoney(b.avg_pnl)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </TableWrapper>
          )}
        </div>
        <div>
          <h4 className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2">Losers</h4>
          {losersTable.length === 0 ? (
            <div className="text-xs text-gray-600 py-4 text-center">No losing exits</div>
          ) : (
            <TableWrapper inset>
              <Thead>
                <Th>Exit</Th>
                <Th className="text-right">Count</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">Avg</Th>
              </Thead>
              <Tbody>
                {losersTable.map(([key, b]) => (
                  <Tr key={key}>
                    <Td className="font-mono text-gray-200">{key}</Td>
                    <Td className="text-right font-mono">{b.count}</Td>
                    <Td className="text-right font-mono text-red-400 font-bold">{fmtMoney(b.total_loss)}</Td>
                    <Td className="text-right font-mono text-red-400">{fmtMoney(b.avg_loss)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </TableWrapper>
          )}
        </div>
          </div>
        </>
      )}
    </CardShell>
  );
}

// Chart 8: tiny per-class equity sparkline. Only renders when the class has
// at least one closed trade.
function ClassSparkline({ trades, signalClass, entryMode }: { trades: any[]; signalClass: string; entryMode: string }) {
  const classTrades = trades
    .filter((t: any) => t.status === 'closed' && t.signal_class === signalClass && t.entry_mode === entryMode && t.close_time)
    .sort((a: any, b: any) => new Date(a.close_time).getTime() - new Date(b.close_time).getTime());
  if (classTrades.length === 0) return null;

  let cum = 0;
  const sparkData = classTrades.map((t: any, i: number) => {
    cum += (t.pnl ?? 0);
    return { i, cum: Number(cum.toFixed(2)) };
  });
  const endCum = sparkData[sparkData.length - 1].cum;

  return (
    <div className="mb-3 -mx-1">
      <ResponsiveContainer width="100%" height={32}>
        <LineChart data={sparkData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <ReferenceLine y={0} stroke="#374151" />
          <Line
            type="monotone"
            dataKey="cum"
            strokeWidth={1.5}
            stroke={endCum >= 0 ? '#4ade80' : '#f87171'}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
